import { useLingui } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import {
  createAction,
  type CreateActionRequest,
  type ManualActionResponse,
} from "@ando/core";
import { toast } from "@ando/ui";

import { activeActionApi } from "./useActiveAction";
import { resolveTabForActionType } from "../actionTypeToTab";
import { proposedFieldsFromManualResponse } from "../utils/manualActionFields";
import { getWorkspaceTabDef } from "@/features/workspace/config";

/**
 * useCreateManualAction — start a MANUAL action from the working area.
 *
 * The user-facing counterpart to an AI proposal: instead of waiting for the
 * agent to propose (e.g.) an invoice, the operator clicks "Nuova fattura"
 * and gets the SAME editable HITL surface, pre-empty (or AI-filled on
 * demand via the "Compila con AI" button — see ``aiFillAction``).
 *
 * DRY by design — this hook does NOT render any UI. It:
 *   1. ``POST /actions`` to mint a server-side proposal in
 *      ``awaiting_confirmation`` (BE reuses the SAME factories the AI
 *      pipeline uses, so the proposal is indistinguishable downstream).
 *   2. Registers it in ``activeActionStore`` — the SAME store AI proposals
 *      live in — so the existing ``FocusedActionView`` mounts it.
 *   3. Navigates to the action's working-area tab with ``?proposal=<id>``,
 *      exactly like clicking a proposal chip. NO drawer / overlay — the
 *      surface renders IN the routed tab (per the working-area rule).
 *
 * From there the user edits the schema-driven form, optionally fills it
 * with AI, and confirms through the unchanged execute pipeline.
 */

export interface CreateManualActionInput extends CreateActionRequest {
  /** Working-area surface title (e.g. "Nuova fattura", "Nuovo cliente"). */
  readonly title: string;
  /** Optional vendor label for the eyebrow (e.g. "Fatture in Cloud"). */
  readonly vendorLabel?: string;
  /** Optional subtitle (entity affected). */
  readonly subtitle?: string;
}

export function useCreateManualAction() {
  const { t } = useLingui();
  const navigate = useNavigate();

  return useMutation<ManualActionResponse, Error, CreateManualActionInput>({
    mutationFn: async ({ title, vendorLabel, subtitle, ...request }) => {
      const res = await createAction(request);
      // Register in the shared store so the existing FocusedActionView
      // renders it identically to an AI proposal. ``schema: []`` → the
      // canonical schema is fetched lazily by ``usePayloadSchema`` on mount
      // (same path AI proposals take), so there's a single schema source.
      activeActionApi.upsert({
        id: res.proposal_id,
        actionType: res.action_type,
        title,
        vendorLabel,
        subtitle,
        schema: [],
        proposedFields: proposedFieldsFromManualResponse(res),
        status: "proposed",
        createdAt: new Date().toISOString(),
      });
      return res;
    },
    onSuccess: (res) => {
      const tab = resolveTabForActionType(res.action_type);
      const def = getWorkspaceTabDef(tab);
      if (!def) {
        toast.error(t`Impossibile aprire l'azione: area non trovata.`);
        return;
      }
      void navigate({ to: def.route, search: { proposal: res.proposal_id } });
    },
    onError: (err) => {
      // Surface the failure — otherwise a click on "+ Nuovo" looks like it
      // does nothing (e.g. when the BE endpoint isn't deployed yet → 404).
      const status = (err as { status?: number } | null)?.status;
      if (status === 404) {
        toast.error(
          t`Creazione manuale non disponibile: il backend non espone ancora questa azione (riavvia/ricostruisci il backend).`,
        );
      } else if (status === 422) {
        toast.error(err.message ?? t`Questo tipo di azione non è ancora creabile manualmente.`);
      } else {
        toast.error(err.message ?? t`Impossibile creare l'azione. Riprova.`);
      }
    },
  });
}
